import React, { useState } from "react";
// eslint-disable-next-line cohere-react/no-mui-styled-import
import { styled, Theme } from "@material-ui/core/styles";
import { Body1 } from "../Typography";
import { Card } from "../Card";
import { colorsDark } from "../../themes";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import TableCell from "./TableCell";
import { CardContent, Collapse } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";

interface Column<T> {
  name: string;
  width: string;
  themedPaddingRight: number;
  value: (arg: T) => React.ReactNode;
  allowTextWrap?: boolean;
  overflow?: string;
}

interface IProps<T> {
  index: number;
  item: T;
  tableColumns: Column<T>[];
  onRowClick?: (row: T, index: number, event: React.MouseEvent<Element, MouseEvent>) => void;
  clickable?: boolean;
  noTextWrap?: boolean;
  onDelete?: (row: T, index: number) => void;
  cardHeight?: "short" | "tall" | "medium";
  wide?: boolean;
  rowExpansion?: (row: T, index: number) => React.ReactNode;
  rowColor?: string;
  rowHoverShadow?: string;
  hideRowHoverBorderColor?: boolean;
  rowActiveBackgroundColor?: string;
  show3linesAndThenEllipsis?: boolean;
  showTotalItems?: boolean;
  showWarningBorder?: boolean;
  selectableRows?: boolean;
  selected?: boolean;
  onSelect?: (isSelected: boolean) => void;
  withCard?: boolean;
  noRowBoxShadow?: boolean;
  disableRowPointer?: boolean;
  customPadding?: string | number;
  customMarginBottom?: string | number;
}

export default function TableRow<T>({
  index,
  item,
  tableColumns,
  onRowClick,
  clickable,
  noTextWrap,
  onDelete,
  cardHeight,
  rowExpansion,
  rowColor,
  wide,
  rowHoverShadow,
  hideRowHoverBorderColor,
  rowActiveBackgroundColor,
  show3linesAndThenEllipsis,
  showTotalItems,
  showWarningBorder,
  selectableRows,
  selected,
  onSelect,
  withCard = true,
  noRowBoxShadow = false,
  disableRowPointer = false,
  customPadding,
  customMarginBottom,
}: IProps<T>) {
  const [expanded, setExpanded] = useState(false);
  let totalItemsCount = 0;
  const linesToDisplayBeforeDisplayingEllipsis = 3; //used in filter tab for Guidelines

  return withCard ? (
    <div>
      <TableCard
        onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
          if (onRowClick) {
            onRowClick(item, index, event);
          }
          setExpanded(!expanded);
        }}
        cardHeight={cardHeight}
        clickable={disableRowPointer ? false : Boolean(clickable || onRowClick)}
        rowColor={rowColor}
        rowHoverShadow={rowHoverShadow}
        hideRowHoverBorderColor={hideRowHoverBorderColor}
        wide={wide}
        rowActiveBackgroundColor={rowActiveBackgroundColor}
        showWarningBorder={showWarningBorder}
        noRowBoxShadow={noRowBoxShadow}
        customPadding={customPadding}
        customMarginBottom={customMarginBottom}
      >
        {selectableRows && (
          <TableCell width="50px" themedPaddingRight={1} overflow="visible">
            <Checkbox
              color="primary"
              checked={selected}
              onChange={(e) => onSelect && onSelect(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              inputProps={{ "aria-label": "select row" }}
              style={{ zIndex: 0 }}
            />
          </TableCell>
        )}
        {tableColumns.map((column, index) => {
          if (column.width !== "0%") {
            const val = column.value(item);
            const isString = typeof val === "string" || val === undefined;
            //Used to count total number of items if you want it displayed
            if (isString && showTotalItems) {
              let itemsArray = val?.split(",");
              totalItemsCount = itemsArray?.length || 0;
            }
            // Allow column to set their own noTextWrap. This overrides noTextWrap props
            let noTextWrapStyle = column.allowTextWrap ? false : noTextWrap;
            return (
              <TableCell
                width={column.width}
                themedPaddingRight={column.themedPaddingRight}
                key={index}
                noTextWrap={noTextWrapStyle}
                overflow={column.overflow}
              >
                {isString ? (
                  //Added the styles twice here since they aren't inherited and was breaking the changes implemented in CLIN-1059
                  <div
                    style={{
                      wordBreak: "break-word",
                      whiteSpace: noTextWrapStyle ? "nowrap" : show3linesAndThenEllipsis ? "pre-line" : undefined,
                      display: show3linesAndThenEllipsis ? "-webkit-box" : undefined,
                      WebkitBoxOrient: show3linesAndThenEllipsis ? "vertical" : undefined,
                      WebkitLineClamp: show3linesAndThenEllipsis ? linesToDisplayBeforeDisplayingEllipsis : undefined,
                      overflow: noTextWrapStyle || show3linesAndThenEllipsis ? "hidden" : undefined,
                      textOverflow: noTextWrapStyle || show3linesAndThenEllipsis ? "ellipsis" : undefined,
                    }}
                  >
                    <Body1
                      style={{
                        wordBreak: "break-word",
                        whiteSpace: noTextWrapStyle ? "nowrap" : show3linesAndThenEllipsis ? "pre-line" : undefined,
                        display: show3linesAndThenEllipsis ? "-webkit-box" : undefined,
                        WebkitBoxOrient: show3linesAndThenEllipsis ? "vertical" : undefined,
                        WebkitLineClamp: show3linesAndThenEllipsis ? linesToDisplayBeforeDisplayingEllipsis : undefined,
                        overflow: noTextWrapStyle || show3linesAndThenEllipsis ? "hidden" : undefined,
                        textOverflow: noTextWrapStyle || show3linesAndThenEllipsis ? "ellipsis" : undefined,
                      }}
                    >
                      {val ? val : "--"}
                    </Body1>
                    {showTotalItems && totalItemsCount > 1 ? (
                      <Body1 color="textSecondary">({totalItemsCount} total)</Body1>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ width: "95%" }}>{val}</div>
                )}
              </TableCell>
            );
          } else {
            return <></>;
          }
        })}
        {!!onDelete && (
          <IconButton onClick={() => onDelete(item, index)} style={{ alignSelf: "center", zIndex: 1 }}>
            <DeleteIcon />
          </IconButton>
        )}
      </TableCard>
      {rowExpansion && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>{rowExpansion(item, index)}</CardContent>
        </Collapse>
      )}
    </div>
  ) : (
    <div>
      <Divider style={{ marginBottom: 0 }} />
      <Row
        slim={false}
        clickable={false}
        onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
          if (onRowClick) {
            onRowClick(item, index, event);
          }
          setExpanded(!expanded);
        }}
        style={{
          backgroundColor: rowColor ? rowColor : "",
          borderLeft: "4px solid rgba(0, 0, 0, 0)",
          paddingLeft: "0",
        }}
      >
        {selectableRows && (
          <TableCell width="50px" themedPaddingRight={1} overflow="visible">
            <Checkbox
              color="primary"
              checked={selected}
              onChange={(e) => onSelect && onSelect(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              inputProps={{ "aria-label": "select row" }}
            />
          </TableCell>
        )}
        {tableColumns.map((column, index) => {
          if (column.width !== "0%") {
            const val = column.value(item);
            const isString = typeof val === "string" || val === undefined;
            //Used to count total number of items if you want it displayed
            if (isString && showTotalItems) {
              let itemsArray = val?.split(",");
              totalItemsCount = itemsArray?.length || 0;
            }
            // Allow column to set their own noTextWrap. This overrides noTextWrap props
            let noTextWrapStyle = column.allowTextWrap ? false : noTextWrap;
            return (
              <TableCell
                width={column.width}
                themedPaddingRight={column.themedPaddingRight}
                key={index}
                noTextWrap={noTextWrapStyle}
                overflow={column?.overflow}
              >
                {isString ? (
                  //Added the styles twice here since they aren't inherited and was breaking the changes implemented in CLIN-1059
                  <div
                    style={{
                      wordBreak: "break-word",
                      whiteSpace: noTextWrapStyle ? "nowrap" : show3linesAndThenEllipsis ? "pre-line" : undefined,
                      display: show3linesAndThenEllipsis ? "-webkit-box" : undefined,
                      WebkitBoxOrient: show3linesAndThenEllipsis ? "vertical" : undefined,
                      WebkitLineClamp: show3linesAndThenEllipsis ? linesToDisplayBeforeDisplayingEllipsis : undefined,
                      overflow: noTextWrapStyle || show3linesAndThenEllipsis ? "hidden" : undefined,
                      textOverflow: noTextWrapStyle || show3linesAndThenEllipsis ? "ellipsis" : undefined,
                    }}
                  >
                    <Body1
                      style={{
                        wordBreak: "break-word",
                        whiteSpace: noTextWrapStyle ? "nowrap" : show3linesAndThenEllipsis ? "pre-line" : undefined,
                        display: show3linesAndThenEllipsis ? "-webkit-box" : undefined,
                        WebkitBoxOrient: show3linesAndThenEllipsis ? "vertical" : undefined,
                        WebkitLineClamp: show3linesAndThenEllipsis ? linesToDisplayBeforeDisplayingEllipsis : undefined,
                        overflow: noTextWrapStyle || show3linesAndThenEllipsis ? "hidden" : undefined,
                        textOverflow: noTextWrapStyle || show3linesAndThenEllipsis ? "ellipsis" : undefined,
                      }}
                    >
                      {val ? val : "--"}
                    </Body1>
                    {showTotalItems && totalItemsCount > 1 ? (
                      <Body1 color="textSecondary">({totalItemsCount} total)</Body1>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ width: "95%" }}>{val}</div>
                )}
              </TableCell>
            );
          } else {
            return <></>;
          }
        })}
        {!!onDelete && (
          <IconButton onClick={() => onDelete(item, index)} style={{ alignSelf: "flex-end", zIndex: 1 }}>
            <DeleteIcon />
          </IconButton>
        )}
      </Row>
      {rowExpansion && rowExpansion(item, index) && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent style={{ padding: 0 }}>{rowExpansion(item, index)}</CardContent>
        </Collapse>
      )}
    </div>
  );
}

// eslint-disable-next-line cohere-react/no-mui-styled-import
export const TableCard = styled(
  ({
    wide,
    cardHeight,
    clickable,
    rowColor,
    rowHoverShadow,
    hideRowHoverBorderColor,
    rowActiveBackgroundColor,
    showWarningBorder,
    noRowBoxShadow,
    customPadding,
    customMarginBottom,
    ...other
  }) => <Card noBoxShadow={!!noRowBoxShadow} {...other} />
)<
  Theme,
  {
    wide?: boolean;
    cardHeight?: string;
    clickable: boolean;
    rowColor?: string;
    rowHoverShadow?: string;
    hideRowHoverBorderColor?: boolean;
    rowActiveBackgroundColor?: string;
    showWarningBorder?: boolean;
    customPadding?: string | number;
    customMarginBottom?: string | number;
  }
>(
  ({
    theme,
    wide,
    cardHeight,
    clickable,
    rowColor,
    rowHoverShadow,
    hideRowHoverBorderColor,
    rowActiveBackgroundColor,
    showWarningBorder,
    customPadding,
    customMarginBottom,
  }) => ({
    display: "flex",
    padding: customPadding || theme.spacing(cardHeight === "tall" ? 4 : cardHeight === "short" ? 0.5 : 2, wide ? 2 : 3),
    marginBottom: customMarginBottom ?? theme.spacing(cardHeight === "tall" ? 4 : cardHeight === "short" ? 2 : 2),
    position: "relative",
    cursor: clickable ? "pointer" : "",
    borderLeft: showWarningBorder ? "12px solid #FFC107" : "none",
    borderTop: "none",
    borderBottom: "none",
    ...(clickable
      ? {
          "&:hover": {
            borderColor: hideRowHoverBorderColor ? "none" : colorsDark.gray.dark,
            boxShadow: rowHoverShadow ? rowHoverShadow : "none",
          },
          "&:active": {
            backgroundColor: rowActiveBackgroundColor ? rowActiveBackgroundColor : "none",
          },
        }
      : {}),
    ...(rowColor
      ? {
          backgroundColor: rowColor,
        }
      : {}),
  })
);

// eslint-disable-next-line cohere-react/no-mui-styled-import
const Row = styled(({ clickable, ...other }) => <div {...other} />)<Theme, { slim: boolean; clickable: boolean }>(
  ({ theme, clickable }) => ({
    padding: theme.spacing(1, 0),
    display: "flex",
    position: "relative",
    ...(clickable
      ? {
          cursor: "pointer",
          "&:hover": {
            opacity: 0.75,
          },
        }
      : {
          "&:hover": {
            opacity: 1,
          },
        }),
  })
);
